import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs/operators';
import { RecoveryCodesResponse, UserSettingsService } from '../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';
import { RecoveryCodesPanelComponent } from '../recovery-codes-panel/recovery-codes-panel.component';
import { isMethodEnrolled, isTwoFactorAlreadyEnabled, TWO_FACTOR_ALREADY_ENABLED_COPY } from './two-factor-already-enabled';
import { PHONE_VALIDATION_MASK } from 'app/directives/validators/string/phone-validator.directive';
import { LegfiPhonePipe } from 'app/pipes/format/legfi-phone.pipe';

interface SmsPhoneForm
{
    phone: FormControl<string>;
}

/** SMS enrollment step (phone, code, show-once recovery codes); the host renders `footer` in its own footer. */
@UntilDestroy()
@Component({
    selector: 'app-sms-auth-setup-step',
    templateUrl: './sms-auth-setup-step.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, ReactiveFormsModule, ControlMessagesComponent, LegfiPhonePipe, RecoveryCodesPanelComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SmsAuthSetupStepComponent implements OnInit, OnDestroy
{
    /**
     * Label for the first-stage dismiss button. Defaults to 'Cancel' (the step is opened directly,
     * so backing out closes it). The gate dialog opens the step from a method chooser, so it passes
     * 'Back' — the dismiss returns to that chooser rather than closing the flow.
     */
    @Input() dismissLabel = 'Cancel';

    @Output() completed = new EventEmitter<void>();
    @Output() cancelled = new EventEmitter<void>();

    /**
     * Fires when the step reaches its show-once recovery-codes stage, so the host can swap its
     * header — the codes render inside the host's surface, not a dialog of their own.
     */
    @Output() showingRecoveryCodes = new EventEmitter<void>();

    @ViewChild('footer', {static: true}) footer: TemplateRef<unknown>;

    /** The app-wide phone mask, also used as the placeholder — mirrors the Cardholders dialog. */
    protected readonly phoneNumberValidationMask = PHONE_VALIDATION_MASK;

    stage: 'phone' | 'code' | 'codes' = 'phone';
    recoveryCodes: string[] = [];
    codesAcknowledged = false;
    phoneForm: FormGroup<SmsPhoneForm>;
    code = '';
    isSending = false;
    isResending = false;
    isSaving = false;
    isSuccess = false;
    hasError = false;

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
            private _fb: NonNullableFormBuilder,
            private _userSettings: UserSettingsService,
            private _growler: GrowlerService,
            private _cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.phoneForm = this._fb.group({
            phone: ['', [Validators.required, Validators.minLength(10)]],
        });
    }

    /** Send the SMS code, then advance to the verify stage. */
    next(): void {
        if (this.phoneForm.invalid) {
            this.phoneForm.controls.phone.markAsTouched();
            this._growler.error('Error', 'Please enter a valid phone number.');
            return;
        }

        this.isSending = true;
        this._cdr.markForCheck();

        this._userSettings.startSmsAuth(this.phoneForm.controls.phone.value).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.isSending = false;
                this.stage = 'code';
                this._cdr.markForCheck();
            },
            error: (response: unknown) => {
                this.isSending = false;
                this._cdr.markForCheck();
                const sendFailed = () => this._growler.error('Error', 'We couldn\'t send a code to that number. Please check it and try again.');
                if (isTwoFactorAlreadyEnabled(response)) {
                    this._confirmAlreadyEnabled(sendFailed);
                    return;
                }
                sendFailed();
            },
        });
    }

    /** Re-send the SMS code to the same number. */
    resendCode(): void {
        if (this.isResending) {
            return;
        }

        this.isResending = true;
        this._cdr.markForCheck();

        this._userSettings.startSmsAuth(this.phoneForm.controls.phone.value).pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.isResending = false;
                this._cdr.markForCheck();
                this._growler.success('Code sent', 'We\'ve sent a new code to your phone.');
            },
            error: (response: unknown) => {
                this.isResending = false;
                this._cdr.markForCheck();
                const resendFailed = () => this._growler.error('Error', 'We couldn\'t resend the code. Please try again.');
                if (isTwoFactorAlreadyEnabled(response)) {
                    this._confirmAlreadyEnabled(resendFailed);
                    return;
                }
                resendFailed();
            },
        });
    }

    get verifyLabel(): string {
        if (this.isSuccess) {
            return 'Verified';
        }
        return this.isSaving ? 'Verifying…' : 'Verify';
    }

    onCodeChange(code: string): void {
        this.code = code ?? '';
    }

    verify(code: string): void {
        if (code.length !== 6 || this.isSaving || this.isSuccess) {
            return;
        }

        this.isSaving = true;
        this._cdr.markForCheck();

        this._userSettings.finishSmsSetup(code, this.phoneForm.controls.phone.value).pipe(
            finalize(() => this.isSaving = false),
            untilDestroyed(this),
        ).subscribe({
            next: (response: RecoveryCodesResponse) => {
                this.isSuccess = true;
                this._cdr.markForCheck();
                this._growler.success('Success', 'SMS has been enabled as a two-factor verification method on your account.');
                this._successTimer = setTimeout(() => this._finish(response?.recoveryCodes ?? []), 800);
            },
            error: (response: unknown) => {
                if (isTwoFactorAlreadyEnabled(response)) {
                    this._confirmAlreadyEnabled(() => this._triggerError());
                    return;
                }
                this._triggerError();
            },
        });
    }

    /**
     * Finish enrollment. When the server issued recovery codes (feature enabled, first method
     * enrolled), hand over to the show-once stage and hold completion until the user acknowledges
     * them; otherwise complete immediately. See google-auth-setup-step for why this renders in
     * place rather than opening a second dialog.
     */
    private _finish(recoveryCodes: string[]): void {
        if (recoveryCodes.length) {
            this.recoveryCodes = recoveryCodes;
            this.stage = 'codes';
            this.showingRecoveryCodes.emit();
            this._cdr.markForCheck();
            return;
        }
        this.completed.emit();
    }

    /** Show-once stage acknowledged — enrollment is complete. */
    onCodesAcknowledged(): void {
        if (!this.codesAcknowledged) {
            return;
        }
        this.completed.emit();
    }

    /** Back from the code stage returns to phone entry; from phone entry it backs out of the step. */
    onBack(): void {
        if (this.stage === 'code') {
            this.stage = 'phone';
            this.code = '';
            this._cdr.markForCheck();
            return;
        }
        this.cancelled.emit();
    }

    ngOnDestroy(): void {
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
    }

    /**
     * Core said SMS is already enabled (SD-10440). Close only if the server confirms SMS is enrolled;
     * otherwise fall back to the original error (review F-f7cf), so a misread can never trap a user.
     */
    private _confirmAlreadyEnabled(otherwise: () => void): void {
        isMethodEnrolled(this._userSettings, 'sms').pipe(untilDestroyed(this)).subscribe((enrolled: boolean) => {
            if (enrolled) {
                this._alreadyEnabled();
            } else {
                otherwise();
            }
        });
    }

    /**
     * SMS is enrolled: a double-submit, a retry, or a stale second tab. Nothing needs fixing, so say
     * so calmly and close as completed — both hosts (Settings and the login gate) read that as enrolled.
     */
    private _alreadyEnabled(): void {
        this._growler.success(TWO_FACTOR_ALREADY_ENABLED_COPY.title, TWO_FACTOR_ALREADY_ENABLED_COPY.message);
        this.completed.emit();
    }

    private _triggerError(): void {
        this.hasError = true;
        this._cdr.markForCheck();
        this._growler.error('Error', 'We were unable to verify that code. Please try again.');
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
            this._cdr.markForCheck();
        }, 600);
    }
}
